import { UIData } from '../../hooks/stores/uiStore';
import { LauncherElementData } from '../../hooks/stores/launcherElementsStore';
import { LauncherStatesData, launcherStatesOrder } from '../../hooks/stores/launcherStatesStore';
import { stateViewsInfo } from '../../info/stateViewsInfo';
import { findNodePath } from '../../functions/launcherElement/findNodePath';
import { updateObjectNestedProperties } from '../../functions/launcherElement/updateObjectNestedProperties';
import { getObjectNestedProperties } from '../../functions/launcherElement/getObjectNestedProperties';
import { convertStringFromCamelCaseToKebabCase } from '../../functions/utils/convertStringFromCamelCaseToKebabCase';

interface VisibilityUtilsDependencies {
  UIData: UIData;
  setUIData: (data: Partial<UIData>) => void;
  launcherElementsData: Record<string, LauncherElementData>;
  setLauncherElementsData: (data: Partial<Record<string, LauncherElementData>>, replace?: boolean) => void;
  launcherStatesData: LauncherStatesData;
  launcherWindowData: {
    children: any[];
  };
}

export function createVisibilityUtils({
  UIData,
  setUIData,
  launcherElementsData,
  setLauncherElementsData,
  launcherStatesData,
  launcherWindowData,
}: VisibilityUtilsDependencies) {
  const _getSelectorString = (
    launcherState?: [keyof LauncherStatesData | null, string | null],
    pseudoclass?: string
  ) => {
    const [state, substate] = launcherState || UIData.currentLauncherState;

    const variantString =
      state === null
        ? ''
        : '.variants.' + convertStringFromCamelCaseToKebabCase(state);

    const substateString = substate === null ? '' : `.${substate}`;

    const pseudoclassString =
      pseudoclass || UIData.pseudoclass ? `.&:${UIData.pseudoclass}` : '';

    return `props.class${variantString}${substateString}${pseudoclassString}`;
  };

  const getElementDirectVisibility = (
    id: string,
    launcherState?: [keyof LauncherStatesData | null, string | null]
  ): boolean | undefined => {
    if (launcherElementsData[id]?.type === 'popup') return undefined;
    if (!launcherState) launcherState = UIData.currentLauncherState;

    const selectorString = _getSelectorString(launcherState, '');
    const { display } = getObjectNestedProperties(
      launcherElementsData[id],
      { display: undefined },
      selectorString
    );

    if (display === 'none') return false;
    if (display === undefined) return undefined;
    return true;
  };

  const getSelectedElementsDirectVisibility = (
    launcherState?: [keyof LauncherStatesData | null, string | null]
  ): boolean | undefined => {
    if (!launcherState) launcherState = UIData.currentLauncherState;

    const directVisibilityArray = UIData.selectedElementIDs.map((id) =>
      getElementDirectVisibility(id, launcherState)
    );

    if (directVisibilityArray.every((isVisible) => isVisible === false)) {
      return false;
    }
    if (directVisibilityArray.every((isVisible) => isVisible === true)) {
      return true;
    }
    return undefined;
  };

  const getElementComputedVisibility = (
    id: string,
    launcherState?: [keyof LauncherStatesData | null, string | null],
    launcherStates?: LauncherStatesData
  ): boolean | undefined => {
    if (!launcherState) launcherState = UIData.currentLauncherState;
    const nodePath = findNodePath(launcherWindowData.children, id);

    const getNodeVisibility = (id: string) => {
      let result = getElementDirectVisibility(id, [null, null]);

      for (const state of launcherStatesOrder) {
        const substate = String((launcherStates || launcherStatesData)[state]);
        const visibility = getElementDirectVisibility(id, [state, substate]);

        if (visibility !== undefined) result = visibility;
        if (launcherState![0] === state) break;
      }

      return result;
    };

    let resultVisibility = undefined;
    for (const nodeID of nodePath) {
      resultVisibility = getNodeVisibility(nodeID);
      if (resultVisibility === false) return false;
    }

    return resultVisibility;
  };

  const getSelectedElementsComputedVisibility = (
    launcherState?: [keyof LauncherStatesData | null, string | null],
    launcherStates?: LauncherStatesData
  ): boolean | undefined => {
    if (!launcherState) launcherState = UIData.currentLauncherState;

    const visibilityArray = UIData.selectedElementIDs.map((id) =>
      getElementComputedVisibility(id, launcherState, launcherStates)
    );
    if (visibilityArray.length === 0) return undefined;

    if (visibilityArray.every((isVisible) => isVisible === true)) return true;
    if (visibilityArray.every((isVisible) => isVisible === false)) return false;
    return undefined;
  };

  const _getRelatedStates = (state: keyof LauncherStatesData | null) => {
    if (!state) return [];
    
    const tasks = ['Install', 'Update', 'Repair', 'Move', 'Uninstall'];

    // Handle "any" state propagation
    if (state.startsWith('isAny') || state.startsWith('hasAny')) {
      const stateType = state.match(/^(isAny|hasAny)(.+?)(InProgress|Paused|Failed)$/)?.[3];
      if (!stateType) return [];

      // For InProgress, affect all states. For others, only affect their own type
      if (stateType === 'InProgress') {
        return [
          ...tasks.map(task => `is${task}TaskInProgress`),
          ...tasks.map(task => `is${task}TaskPaused`),
          ...tasks.map(task => `has${task}TaskFailed`),
          'isAnyTaskInProgress',
          'isAnyTaskPaused',
          'hasAnyTaskFailed'
        ] as (keyof LauncherStatesData | null)[];
      } else {
        return [
          ...tasks.map(task => 
            stateType === 'Paused' 
              ? `is${task}TaskPaused` 
              : `has${task}TaskFailed`
          ),
          stateType === 'Paused' 
            ? 'isAnyTaskPaused' 
            : 'hasAnyTaskFailed'
        ] as (keyof LauncherStatesData | null)[];
      }
    }

    // Handle individual task state propagation
    if (state.match(/(InProgress|Paused|Failed)$/)) {
      const task = state.match(/^(is|has)(.+?)Task(InProgress|Paused|Failed)$/)?.[2];
      const stateType = state.match(/^(is|has)(.+?)Task(InProgress|Paused|Failed)$/)?.[3];
      if (!task || !stateType) return [];

      // For InProgress, affect all states for this task only. For others, only affect their own type
      if (stateType === 'InProgress') {
        return [
          `is${task}TaskInProgress`,
          `is${task}TaskPaused`,
          `has${task}TaskFailed`
        ] as (keyof LauncherStatesData)[];
      } else {
        // For Paused and Failed, only affect their own type
        return [
          stateType === 'Paused' 
            ? `is${task}TaskPaused` 
            : `has${task}TaskFailed`
        ] as (keyof LauncherStatesData)[];
      }
    }

    return [];
  };

  const _handleDefaultStateChange = (element: LauncherElementData, propValue: string | undefined) => {
    let updatedElement = element;

    // Set base visibility
    updatedElement = updateObjectNestedProperties(
      updatedElement,
      { display: propValue },
      'props.class'
    );

    // Set all state visibilities
    Object.values(stateViewsInfo).forEach(({ significantLauncherState }) => {
      const selectorString = _getSelectorString(significantLauncherState, '');
      updatedElement = updateObjectNestedProperties(
        updatedElement,
        { display: propValue },
        selectorString
      );
    });

    // Set all "any" states
    ['isAnyTaskInProgress', 'isAnyTaskPaused', 'hasAnyTaskFailed'].forEach(anyState => {
      const selectorString = _getSelectorString([anyState as keyof LauncherStatesData, 'true'], '');
      updatedElement = updateObjectNestedProperties(
        updatedElement,
        { display: propValue },
        selectorString
      );
    });

    return updatedElement;
  };

  const _handleAnyStateChange = (
    element: LauncherElementData,
    launcherState: [keyof LauncherStatesData | null, string | null],
    propValue: string | undefined,
    relatedStates: (keyof LauncherStatesData | null)[]
  ) => {
    let updatedElement = element;

    if (!UIData.useDirectVisibility) {
      // Regular mode - just update the current state and related states
      const anySelectorString = _getSelectorString(launcherState, '');
      updatedElement = updateObjectNestedProperties(
        updatedElement,
        { display: propValue },
        anySelectorString
      );

      relatedStates.forEach(state => {
        const stateSelectorString = _getSelectorString([state as keyof LauncherStatesData, 'true'], '');
        updatedElement = updateObjectNestedProperties(
          updatedElement,
          { display: propValue },
          stateSelectorString
        );
      });
    } else {
      // Direct mode - update states based on type
      const stateType = launcherState[0]?.match(/^(isAny|hasAny)(.+?)(InProgress|Paused|Failed)$/)?.[3];
      if (!stateType) return updatedElement;

      const tasks = ['Install', 'Update', 'Repair', 'Move', 'Uninstall'];
      
      if (stateType === 'InProgress') {
        // InProgress affects all states
        const allStates = [
          ...tasks.flatMap(task => [
            `is${task}TaskInProgress`,
            `is${task}TaskPaused`,
            `has${task}TaskFailed`
          ]),
          'isAnyTaskInProgress',
          'isAnyTaskPaused',
          'hasAnyTaskFailed'
        ];

        allStates.forEach(state => {
          const isAnyState = state.startsWith('isAny') || state.startsWith('hasAny');
          const stateSelectorString = _getSelectorString(
            [state as keyof LauncherStatesData, isAnyState ? launcherState[1] : 'true'],
            ''
          );
          updatedElement = updateObjectNestedProperties(
            updatedElement,
            { display: propValue },
            stateSelectorString
          );
        });
      } else {
        // Paused and Failed only affect their own type
        const statesToUpdate = [
          ...tasks.map(task => 
            stateType === 'Paused' 
              ? `is${task}TaskPaused` 
              : `has${task}TaskFailed`
          ),
          // Only include the corresponding "any" state
          stateType === 'Paused' 
            ? 'isAnyTaskPaused' 
            : 'hasAnyTaskFailed'
        ];

        // Only update the specific state type
        statesToUpdate.forEach(state => {
          const isAnyState = state.startsWith('isAny') || state.startsWith('hasAny');
          const stateSelectorString = _getSelectorString(
            [state as keyof LauncherStatesData, isAnyState ? launcherState[1] : 'true'],
            ''
          );
          updatedElement = updateObjectNestedProperties(
            updatedElement,
            { display: propValue },
            stateSelectorString
          );
        });
      }
    }

    return updatedElement;
  };

  const _handleIndividualStateChange = (
    element: LauncherElementData,
    launcherState: [keyof LauncherStatesData | null, string | null],
    propValue: string | undefined,
    relatedStates: (keyof LauncherStatesData | null)[]
  ) => {
    let updatedElement = element;

    // Update the current state and all related states
    relatedStates.forEach(state => {
      // For "any" states, we need to use the original state value ('true')
      const isAnyState = state?.startsWith('isAny') || state?.startsWith('hasAny');
      const stateSelectorString = _getSelectorString(
        [state as keyof LauncherStatesData, isAnyState ? launcherState[1] : 'true'], 
        ''
      );
      
      updatedElement = updateObjectNestedProperties(
        updatedElement,
        { display: propValue },
        stateSelectorString
      );
    });

    return updatedElement;
  };

  const _handleStateChange = (
    element: LauncherElementData,
    launcherState: [keyof LauncherStatesData | null, string | null],
    propValue: string | undefined,
    defaultVisibility: string
  ) => {
    let updatedElement = element;
    const relatedStates = _getRelatedStates(launcherState[0]);

    // If it's a non-task state, just update that specific state
    if (launcherState[0] && !launcherState[0].match(/(Task|Any)/)) {
      const selectorString = _getSelectorString(launcherState, '');
      return updateObjectNestedProperties(
        updatedElement,
        { display: propValue },
        selectorString
      );
    }

    Object.values(stateViewsInfo).forEach(({ significantLauncherState }) => {
      const [stateKey, stateValue] = significantLauncherState;
      const selectorString = _getSelectorString(significantLauncherState, '');
      const isRelatedState = relatedStates.includes(stateKey);

      if (isRelatedState) {
        updatedElement = updateObjectNestedProperties(
          updatedElement,
          { display: propValue },
          selectorString
        );
      }
    });

    // Handle "any" state changes
    if (launcherState[0]?.startsWith('isAny') || launcherState[0]?.startsWith('hasAny')) {
      updatedElement = _handleAnyStateChange(updatedElement, launcherState, propValue, relatedStates);
    } else {
      updatedElement = _handleIndividualStateChange(updatedElement, launcherState, propValue, relatedStates);
    }

    return updatedElement;
  };

  const setElementVisibility = (
    id: string,
    value?: boolean,
    launcherState?: [keyof LauncherStatesData | null, string | null]
  ) => {
    if (!launcherState) launcherState = UIData.currentLauncherState;
    const propValue = value === true ? 'flex' : value === false ? 'none' : undefined;
    
    const element = launcherElementsData[id];
    if (!element) return;

    // Special case: Reset all states visibility when value is undefined
    if (value === undefined && launcherState[0] === null) {
        let updatedElement = JSON.parse(JSON.stringify(element));
        
        // Reset base visibility
        updatedElement = updateObjectNestedProperties(
            updatedElement,
            { display: undefined },
            'props.class'
        );

        // Reset all state visibilities
        Object.values(stateViewsInfo).forEach(({ significantLauncherState }) => {
            const selectorString = _getSelectorString(significantLauncherState, '');
            updatedElement = updateObjectNestedProperties(
                updatedElement,
                { display: undefined },
                selectorString
            );
        });

        // Reset all "any" states
        ['isAnyTaskInProgress', 'isAnyTaskPaused', 'hasAnyTaskFailed'].forEach(anyState => {
            const selectorString = _getSelectorString([anyState as keyof LauncherStatesData, 'true'], '');
            updatedElement = updateObjectNestedProperties(
                updatedElement,
                { display: undefined },
                selectorString
            );
        });

        setLauncherElementsData({ [id]: updatedElement });
        return;
    }

    // Handle regular visibility changes
    if (!UIData.useDirectVisibility) {
        const selectorString = _getSelectorString(launcherState, '');
        const updatedElement = updateObjectNestedProperties(
          element,
          { display: propValue },
          selectorString
        );
        setLauncherElementsData({ [id]: updatedElement });
        return;
    }

    // Handle direct visibility mode
    let updatedElement = JSON.parse(JSON.stringify(element));
    const isDefaultState = launcherState[0] === null;
    const defaultVisibility = getElementComputedVisibility(id, [null, null]) === false ? 'none' : 'flex';

    // Set default visibility for undefined states
    const allPossibleStates = [
      ...Object.values(stateViewsInfo).map(({ significantLauncherState }) => significantLauncherState),
      ['isAnyTaskInProgress', 'true'],
      ['isAnyTaskPaused', 'true'],
      ['hasAnyTaskFailed', 'true']
    ] as [keyof LauncherStatesData | null, string | null][];

    allPossibleStates.forEach(state => {
      const selectorString = _getSelectorString(state, '');
      const hasDefinedValue = getObjectNestedProperties(updatedElement, { display: undefined }, selectorString).display !== undefined;
      
      if (!hasDefinedValue) {
        updatedElement = updateObjectNestedProperties(
          updatedElement,
          { display: defaultVisibility },
          selectorString
        );
      }
    });

    // Handle default state changes
    if (isDefaultState) {
      updatedElement = _handleDefaultStateChange(updatedElement, propValue);
    } else {
      updatedElement = _handleStateChange(updatedElement, launcherState, propValue, defaultVisibility);
    }

    setLauncherElementsData({ [id]: updatedElement });
  };

  const setSelectedElementsVisibility = (
    value?: boolean,
    launcherState?: [keyof LauncherStatesData | null, string | null]
  ) => {
    if (!launcherState) launcherState = UIData.currentLauncherState;
    UIData.selectedElementIDs.forEach((id) =>
      setElementVisibility(id, value, launcherState)
    );
  };

  return {
    getElementDirectVisibility,
    getSelectedElementsDirectVisibility,
    getElementComputedVisibility,
    getSelectedElementsComputedVisibility,
    setElementVisibility,
    setSelectedElementsVisibility,
  };
} 